import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Isvg from 'react-inlinesvg';
import _isNil from 'lodash/isNil';

import Message from 'components/Message';
import { TooltipContent, TooltipToggle } from 'components/Tooltip';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import { bem, classNameWithModifiers as withMods } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import { selectRouteParams } from 'selectors/general';
import { selectIsMobile } from 'selectors/ui';

import ServicesContext from '../ServicesContext';
import { messages } from '../Services.messages';

import './ServicesListItem.scss';

const { block, element } = bem('services-list-item');

const ServicesListItem = ({ id, name, duration, practitioners, resources, price }) => {
  const navigate = useNavigate();
  const routeParams = useSelector(selectRouteParams);
  const isMobile = useSelector(selectIsMobile);
  const { selectService, selectedService } = useContext(ServicesContext);
  const practitionersLength = practitioners?.length;
  const equipmentLength = resources?.length;
  const isSelected = id === selectedService?.id;
  const isDisabled = !practitionersLength && !equipmentLength;
  const hasPrice = !_isNil(price);

  function selectServiceAndNavigate(serviceId) {
    selectService(serviceId);

    const newPath = makePath.manageServices({
      ...routeParams,
      itemId: id,
      focus: isMobile ? 'details' : 'list',
    });

    navigate(newPath);
  }

  return (
    <li className={withMods(block(), { isDisabled: isDisabled || !hasPrice })}>
      {isSelected && <span className={element('selected-marker')} />}
      <button type="button" className={element('button')} onClick={() => selectServiceAndNavigate(id)}>
        <When predicate={!hasPrice}>
          <TooltipToggle name="service-pending-review-tooltip">
            <Isvg src={ICONS.clock} className={element('pending-review-icon')} />
          </TooltipToggle>
          <TooltipContent name="service-pending-review-tooltip" idempotent>
            <Message message={messages.list.pendingReviewTooltip} />
          </TooltipContent>
        </When>
        <div className={element('name')}>
          <Message
            message={messages.list.itemNameDuration}
            values={{ duration, serviceName: name, grey: text => <span className={element('grey-text')}>{text}</span> }}
          />
          <When predicate={isDisabled}>
            <TooltipToggle name="service-disabled-tooltip">
              <Isvg src={ICONS.infoYellow} />
            </TooltipToggle>
            <TooltipContent name="service-disabled-tooltip">
              <Message message={messages.list.disabledTooltip} />
            </TooltipContent>
          </When>
        </div>
        <When predicate={!!practitionersLength}>
          <Message
            as="div"
            className="text--ui70"
            message={messages.list.practitionersCount}
            values={{ practitionersCount: practitionersLength }}
          />
        </When>
        <When predicate={!!equipmentLength}>
          <Message
            as="div"
            className="text--ui70"
            message={messages.list.equipmentCount}
            values={{ equipmentCount: equipmentLength }}
          />
        </When>
      </button>
    </li>
  );
};

ServicesListItem.propTypes = {
  id: PropTypes.number,
  name: PropTypes.string,
  duration: PropTypes.string,
  practitioners: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, id: PropTypes.number })),
  resources: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, id: PropTypes.number })),
  price: PropTypes.number,
};

export default ServicesListItem;
